import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/common/Navbar';
import Footer from '../components/common/Footer';
import CartDrawer from '../components/common/CartDrawer';
import api from '../utils/api';

function StockGroupCard({ group }) {
  const [search, setSearch] = useState('');

  const items = useMemo(
    () => group.items.filter(i => i.productName.toLowerCase().includes(search.toLowerCase())),
    [group.items, search]
  );

  return (
    <div className="bg-white border border-outline-variant/20 flex flex-col">
      {/* Card header */}
      <div className="bg-on-surface text-white px-5 py-4">
        <h3 className="font-sans  text-base uppercase tracking-tight leading-tight">{group.groupName}</h3>
        <p className="font-sans text-[10px] text-gray-400 mt-1 uppercase tracking-widest">
          Updated {new Date(group.updatedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
          {' · '}{group.items.length} product{group.items.length !== 1 ? 's' : ''}
        </p>
      </div>

      {/* Search (only worth showing for longer lists) */}
      {group.items.length > 8 && (
        <div className="px-4 pt-3">
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={`Search in ${group.groupName}...`}
            className="input-field py-2 text-sm"
          />
        </div>
      )}

      {/* Table */}
      <div className="overflow-y-auto max-h-96 mt-3">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-surface-container">
            <tr>
              <th className="text-left px-4 py-2 font-label text-[10px] uppercase tracking-widest text-secondary">Product Name</th>
              <th className="text-right px-4 py-2 font-label text-[10px] uppercase tracking-widest text-secondary">Qty</th>
              <th className="text-right px-4 py-2 font-label text-[10px] uppercase tracking-widest text-secondary">Rate</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, i) => (
              <tr key={i} className="border-t border-surface-container">
                <td className="px-4 py-2 font-body text-on-surface">{item.productName}</td>
                <td className="px-4 py-2 text-right font-label font-bold text-on-surface">{item.quantity}</td>
                <td className="px-4 py-2 text-right font-label text-secondary">{item.rate != null ? `₹${item.rate}` : '—'}</td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={3} className="text-center py-8 text-secondary font-body text-sm">No matches.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function CurrentStockPage() {
  const [groups,  setGroups]  = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/stock-groups')
      .then(r => setGroups(r.data.data || []))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <CartDrawer />

      <main className="flex-1 pt-20">
        {/* Header */}
        <div className="bg-on-surface text-white px-6 md:px-10 py-10">
          <div className="max-w-screen-2xl mx-auto">
            <div className="flex items-center gap-2 font-label text-xs text-gray-500 uppercase tracking-widest mb-3">
              <Link to="/" className="hover:text-gray-300">Home</Link>
              <span>/</span>
              <span className="text-gray-400">Current Stock</span>
            </div>
            <div className="max-w-2xl">
              <div className="accent-bar" />
              <h1 className="font-headline  text-5xl md:text-6xl uppercase tracking-tight text-white">
                Current Stock
              </h1>
              <p className="font-body text-gray-400 mt-4 leading-relaxed">
                Live stock quantities and rates, updated directly from our inventory records.
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-screen-2xl mx-auto px-6 md:px-10 py-16">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {[...Array(4)].map((_, i) => <div key={i} className="h-96 skeleton" />)}
            </div>
          ) : groups.length === 0 ? (
            <div className="text-center py-24">
              <span className="material-symbols-outlined text-[80px] text-outline-variant">table_chart</span>
              <h3 className="font-headline font-black text-2xl uppercase mt-4 text-secondary">Stock data not available right now</h3>
              <p className="font-body text-sm text-secondary mt-2">Please check back shortly.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {groups.map(g => <StockGroupCard key={g._id} group={g} />)}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
